import { Dispatch, SetStateAction, useState } from 'react';

import { AddIcon, Button, DeleteIcon, Text, dialogService, toastService } from '@thefarmersfront/kitchen';

import type { IRowNode } from 'ag-grid-community';

import { Table } from '~/components/common';

import { useModal } from '~/hooks/common';
import { useGetOwnerManagerList, usePatchOwnerManagerDelete } from '~/hooks/vender';
import { useLoadingStore } from '~/store';
import { getVenderConfirmManagerInfoColumnDefs } from '~/utils';

import { TOAST_MESSAGE } from '~/constants';

import { VenderOwnerManagerAddModal, VenderOwnerManagerModifyModal } from '..';

import type { TypeErrorResponseData } from '~/types/common.types';
import type { TypeOwnerManagerDetail } from '~/types/vender.types';

import styles from './VenderConfirmManagerInfoSection.module.scss';

type TypeVenderConfirmManagerInfoSectionProps = {
  hasEditPermission: boolean;
  managerAccountLimit: number;
  managerInfo: TypeOwnerManagerDetail[];
  setManagerInfo: Dispatch<SetStateAction<TypeOwnerManagerDetail[]>>;
};

/**
 * [화주사 정보 관리 > 화주사 정보] 담당자 정보 영역
 */
const VenderConfirmManagerInfoSection = ({
  hasEditPermission,
  managerAccountLimit,
  managerInfo,
  setManagerInfo,
}: TypeVenderConfirmManagerInfoSectionProps) => {
  const { getOwnerManagerList } = useGetOwnerManagerList();
  const { patchOwnerManagerDelete } = usePatchOwnerManagerDelete();

  const { setIsLoading } = useLoadingStore();

  const [selectedManagerInfo, setSelectedManagerInfo] = useState<TypeOwnerManagerDetail | null>(null);
  const [selectedTableRows, setSelectedTableRows] = useState<IRowNode<TypeOwnerManagerDetail>[]>([]);

  const {
    isOpen: isManagerAddModalOpen,
    handleOpenModal: handleOpenManagerAddModal,
    handleCloseModal: handleCloseManagerAddModal,
    handleConfirmModal: handleConfirmManagerAddModal,
  } = useModal({
    onConfirm: () => fetchManagerInfo(),
  });

  const {
    isOpen: isManagerModifyModalOpen,
    handleOpenModal: handleOpenManagerModifyModal,
    handleCloseModal: handleCancelManagerModifyModal,
    handleConfirmModal: handleConfirmManagerModifyModal,
  } = useModal({
    onConfirm: () => fetchManagerInfo(),
  });

  const { gridOptions, columnDefs } = getVenderConfirmManagerInfoColumnDefs({
    hasEditPermission,
    onClickManagerModify: (node: IRowNode<TypeOwnerManagerDetail>) => handleClickManagerModify(node),
  });

  const fetchManagerInfo = async () => {
    try {
      setIsLoading(true);

      const _managerInfo = await getOwnerManagerList();

      setManagerInfo(_managerInfo);
    } catch (error) {
      toastService.addToast({
        type: 'error',
        message: (error as TypeErrorResponseData['error'])?.message || TOAST_MESSAGE.DEFAULT_ERROR,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchManagerDelete = async () => {
    const selectedLoginIds = selectedTableRows.map(({ data }) => data?.ownerLoginId as string);

    try {
      setIsLoading(true);

      await patchOwnerManagerDelete({ ownerLoginIds: selectedLoginIds });

      toastService.addToast({
        message: '성공적으로 저장되었습니다.',
      });

      await fetchManagerInfo();
    } catch (error) {
      toastService.addToast({
        type: 'error',
        message: (error as TypeErrorResponseData['error'])?.message || TOAST_MESSAGE.DEFAULT_ERROR,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClickManagerModify = ({ data }: IRowNode<TypeOwnerManagerDetail>) => {
    if (!data) return;

    handleOpenManagerModifyModal();
    setSelectedManagerInfo(data);
  };

  const handleClickManagerDelete = () => {
    if (selectedTableRows.length === 0) {
      return;
    }

    dialogService.addDialog({
      type: 'confirm',
      status: 'error',
      children: (
        <span>
          해당 아이디는 즉시 삭제됩니다.
          <br />
          담당자정보를 정말 삭제하시겠습니까?
        </span>
      ),
      confirmText: '삭제',
      onConfirm: fetchManagerDelete,
    });
  };

  return (
    <>
      <div className={styles['table-section']}>
        <div className={styles['table-section-title-wrapper']}>
          <div className={styles['table-section-title-side-field']}>
            <Text size="xxlarge" weight="bold" element="span">
              담당자 정보
            </Text>
            <Text weight="semi-bold" color="tertiary" element="span">
              {`${managerInfo.length}/${managerAccountLimit}`}
            </Text>
          </div>
          {hasEditPermission && (
            <div className={styles['table-section-title-side-field']}>
              <Button
                size="small"
                color="tertiary"
                variant="outlined"
                leftIcon={<DeleteIcon size={16} />}
                disabled={selectedTableRows.length === 0}
                onClick={handleClickManagerDelete}
              >
                삭제
              </Button>
              <Button
                size="small"
                color="secondary"
                leftIcon={<AddIcon color="white" size={16} />}
                disabled={managerInfo.length >= managerAccountLimit}
                onClick={handleOpenManagerAddModal}
              >
                추가
              </Button>
            </div>
          )}
        </div>
        <div className={styles['table-section-table-wrapper']}>
          <Table
            data={managerInfo}
            options={{ columnDefs, ...gridOptions }}
            onSelectionChange={(row) => setSelectedTableRows(row as IRowNode<TypeOwnerManagerDetail>[])}
          />
        </div>
      </div>
      {isManagerModifyModalOpen && (
        <VenderOwnerManagerModifyModal
          open={isManagerModifyModalOpen}
          managerInfo={selectedManagerInfo}
          onCancel={handleCancelManagerModifyModal}
          onConfirm={handleConfirmManagerModifyModal}
        />
      )}
      {isManagerAddModalOpen && (
        <VenderOwnerManagerAddModal
          open={isManagerAddModalOpen}
          onCancel={handleCloseManagerAddModal}
          onConfirm={handleConfirmManagerAddModal}
        />
      )}
    </>
  );
};

export default VenderConfirmManagerInfoSection;
